/**
 * activity_logs labels for master-data records — Name(CODE) in the message field.
 * activity_logs.action + action_data_id → see masterDataLogAction.ts (keep in lockstep).
 * Keep in lockstep with erp_api/routes/controllers/recordLogLabel.ts
 */

import { wrapLogBold } from 'pages/Logs/logFormat';

/** Staff-facing code on a record row (employeeNumber for employees, else data.code). */
export const recordStaffCodeFromFields = (
  kind: string,
  fields?: Record<string, unknown> | null,
): string => {
  const f = fields || {};
  if (String(kind || '').trim() === 'employee') {
    return String(f.employeeNumber || f.code || '').trim().toUpperCase();
  }
  return String(f.code || '').trim().toUpperCase();
};

/** Plain label for logs/search: Name(CODE). No space before parentheses. */
export const formatRecordLogLabel = (
  name?: string | null,
  code?: string | null,
  fallbackId?: string | null,
): string => {
  const n = String(name || '').trim();
  const c = String(code || '').trim();
  if (n && c) return `${n}(${c})`;
  if (n) return n;
  if (c) return c;
  const id = String(fallbackId || '').trim();
  return id || 'Record';
};

/**
 * Split Name(CODE) back to parts — used so /logs Groups never creates a second
 * location/department node from a Name(CODE) tracker value.
 */
export const parseRecordLogLabelParts = (
  raw?: string | null,
): { name: string, code: string } => {
  const text = String(raw || '').trim();
  if (!text) return { name: '', code: '' };
  // Trailing (CODE) only — e.g. Nigeria(LOC-001), Engineering(DEPT-001).
  const match = text.match(/^(.*)\(([A-Za-z][A-Za-z0-9_-]*)\)\s*$/);
  if (!match) return { name: text, code: '' };
  const name = String(match[1] || '').trim();
  const code = String(match[2] || '').trim();
  if (!name) return { name: text, code: '' };
  return { name, code };
};

/** Bold-wrapped Name(CODE) for activity_logs.message. */
export const wrapLogRecordLabel = (
  name?: string | null,
  code?: string | null,
  fallbackId?: string | null,
): string => wrapLogBold(formatRecordLogLabel(name, code, fallbackId));
